import {Node, Edge} from 'reactflow';

import {GetHandlesFunc} from 'ui-library/reactflow/handles/useGetNodeHandlesData';
import {assertNever} from 'util/Types';

import type {DimensionsForLayout} from './calculateDimensionsForLayout';
import dagreLayout from './dagre';
import elkLayout from './elk';
import {LayoutAlgorithmName} from './layoutAlgorithmConstants';

export {DEFAULT_LAYOUT_ALGORITHM, LayoutAlgorithmName} from './layoutAlgorithmConstants';

/**
 * Holds the node and edge data used by a layout algorithm.
 */
export interface FlowchartData<N extends Node, E extends Edge> {
  nodes: Array<N>;
  edges: Array<E>;
  getHandles?: GetHandlesFunc;
}

/**
 * The type of a function that implements a graph layout algorithm.
 *
 * Layout algorithms should return edges with updated positions and edges optionally updated with
 * data specifying their paths. The nodes and edges being updated may have been hidden by the client
 * so that they do not render until their positions have been calculated. Layout algorithms should use
 * `unhideFlowchartElementAfterLayout` to un-hide the nodes and edges before returning them.
 *
 * @param nodes The nodes to layout.
 * @param edges The edges to layout.
 * @param dimensions The dimensions of the nodes and edges.
 * @returns The nodes and edges with their positions updated.
 */
export type LayoutAlgorithm<N extends Node, E extends Edge> = (
  {nodes, edges}: FlowchartData<N, E>,
  dimensions: DimensionsForLayout
) => Promise<FlowchartData<N, E>>;

/**
 * Get a layout algorithm by name.
 *
 * @param algorithm The name of the layout algorithm to get.
 * @returns The layout algorithm.
 */
export function getLayoutAlgorithm<N extends Node, E extends Edge>(
  algorithm: LayoutAlgorithmName
): LayoutAlgorithm<N, E> {
  switch (algorithm) {
    case LayoutAlgorithmName.DAGRE:
      return dagreLayout;
    case LayoutAlgorithmName.ELK:
      return elkLayout;
    default:
      throw new Error(`Unknown layout algorithm ${assertNever(algorithm)}`);
  }
}
